/**
 * Puts an HTML document on the canvas as a live block, the way the tour
 * seeds its artifacts: the file goes up as a canvas record and the node is
 * added once its bytes are up, marked as a block so it renders live at the
 * box it was written for.
 */

import type { CanvasNodeSource } from "@/analytics/surfaces";
import { performFileRecordUpload } from "@/hooks/useFileRecordUpload";
import { markInsertSource } from "@/pages/canvas/analytics/insertSource";
import {
  blockMarkersFromRecord,
  buildFileRecordEntityForCanvas,
} from "@/pages/canvas/utils/imageDimensions";
import type { FileRecord } from "@cohesive/api-client";
import type { QueryClient } from "@tanstack/react-query";
import type { Node } from "@xyflow/react";

export interface PlaceHtmlBlockArgs {
  canvasId: string;
  queryClient: QueryClient;
  canvas: { addNode: (node: Node) => void };
  file: string;
  html: string;
  position: { x: number; y: number };
  width: number;
  height: number;
  chromeless?: boolean;
  source: CanvasNodeSource;
  /** Asked just before the node goes on; false leaves the record off the canvas. */
  admit?: () => boolean;
}

/** Resolves to the record's id once the node is on the canvas, or "" if refused. */
export async function placeHtmlBlock({
  canvasId,
  queryClient,
  canvas,
  file,
  html,
  position,
  width,
  height,
  chromeless = false,
  source,
  admit,
}: PlaceHtmlBlockArgs): Promise<string> {
  const display = { mode: "block" as const, chromeless, width, height };
  let placed = "";
  await performFileRecordUpload({
    file: new File([html], file, { type: "text/html" }),
    target: { class: "canvas", canvas_id: canvasId },
    queryClient,
    onUploaded: (record: FileRecord) => {
      if (admit && !admit()) return;
      const { entity } = buildFileRecordEntityForCanvas({
        record: { ...record, display },
        contentType: "text/html",
      });
      markInsertSource(source);
      canvas.addNode({
        id: record.id,
        type: "entity",
        position,
        width,
        height,
        data: { entity, ...blockMarkersFromRecord({ display }) },
      } as Node);
      placed = record.id;
    },
  });
  return placed;
}
